import React from "react";
import { graphqlOperation } from 'aws-amplify'
import { Connect } from 'aws-amplify-react'
import { listMarkets } from '../graphql/queries'
import { onCreateMarket } from '../graphql/subscriptions'
import { Loading } from "element-react";
import ProductCard from "../components/ProductItem"
import { Link } from 'react-router-dom'
import Error from './Error'
import styled from 'styled-components';
import ProductSearch from './search/ProductSearch'
import { GNav, GNavbarContainer, GbarLink, TypeScroller } from './TypeNav'

/* ---------- section + market blocks ---------- */
export const SectionTitle = styled.h2`
  max-width: 1180px;
  margin: 20px auto 4px;
  padding: 0 16px;
  color: #1f2d3d;
  font-size: var(--text-2xl);
  font-weight: 800;
  display: flex;
  align-items: center;
  gap: 10px;

  @media screen and (min-width: 768px) {
    padding: 0 24px;
  }
`;

export const MarketsWrap = styled.div`
  max-width: 1180px;
  margin: 0 auto;
  padding: 8px 16px 40px;

  @media screen and (min-width: 768px) {
    padding: 8px 24px 48px;
  }
`;

export const MarketBlock = styled.div`
  margin: 30px 0;
  scroll-margin-top: 170px;
`;

export const MarketHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  background: #fff;
  border: 1px solid rgba(31, 45, 61, 0.08);
  border-left: 4px solid #01bf71;
  border-radius: 14px;
  padding: 14px 18px;
  box-shadow: 0 6px 18px rgba(31, 45, 61, 0.05);
  margin-bottom: 16px;
`;

export const MarketNameWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

export const MarketName = styled(Link)`
  font-size: var(--text-lg);
  font-weight: 700;
  color: #1f2d3d;
  text-decoration: none;

  &:hover {
    color: #01bf71;
  }
`;

export const CountBadge = styled.span`
  flex: 0 0 auto;
  background: rgba(1, 191, 113, 0.12);
  color: #0a8f57;
  font-weight: 700;
  font-size: var(--text-xs);
  padding: 4px 10px;
  border-radius: 999px;
  white-space: nowrap;
`;

export const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

export const TagPill = styled.span`
  background: rgba(255, 154, 60, 0.14);
  color: #c96a12;
  font-size: var(--text-xs);
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 999px;
  white-space: nowrap;
`;

/* bulletproof responsive product grid (plain breakpoints) */
export const ProductGrid = styled.div`
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(2, 1fr); /* 2 columns on mobile */

  @media (min-width: 580px) {
    gap: 12px;
  }
  @media (min-width: 640px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (min-width: 960px) {
    grid-template-columns: repeat(4, 1fr);
  }
  @media (min-width: 1280px) {
    grid-template-columns: repeat(5, 1fr);
  }
`;

export const ViewAll = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100%;
  border: 2px dashed rgba(1, 191, 113, 0.4);
  border-radius: 12px;
  color: #0a8f57;
  font-weight: 700;
  text-decoration: none;
  padding: 24px;
  text-align: center;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: rgba(1, 191, 113, 0.06);
    border-color: #01bf71;
  }
`;

const MarketList = () => {

  const onNewMarket = (prevQuery, newData) => {
    let updatedQuery = { ...prevQuery };

    const updatedMarketList = [
      newData.onCreateMarket,
      ...prevQuery.listMarkets.items
    ];
    updatedQuery.listMarkets.items = updatedMarketList;
    return updatedQuery;
  }

  return (
    <Connect
      query={graphqlOperation(listMarkets)}
      subscription={graphqlOperation(onCreateMarket)}
      onSubscriptionMsg={onNewMarket}
    >
      {({ data, loading, errors }) => {
        if (errors && errors.length > 0) return <Error errors={errors} />
        if (loading || !data || !data.listMarkets) return <Loading fullscreen={true} />
        const markets = data.listMarkets.items

        return (
          <>
            <ProductSearch markets={markets}>
            <SectionTitle>
              <span role="img" aria-label="shopping cart">🛒</span> Markets
            </SectionTitle>

            <GNav aria-label="Product categories">
              <GNavbarContainer>
                <TypeScroller>
                  {markets.map(market => (
                    <GbarLink to={`${market.name}`}
                      key={market.id}
                      smooth={true}
                      duration={500}
                      spy={true}
                      exact='true'
                      offset={-80}
                      className="bg-primary text-white display-inline-block rounded-corners-big item-price pleftright" >
                      {market.name}
                    </GbarLink>
                  ))}
                </TypeScroller>
              </GNavbarContainer>
            </GNav>

            <MarketsWrap>
              {markets.map(market => (
                <MarketBlock key={market.id} id={market.name}>
                  <MarketHeader>
                    <MarketNameWrap>
                      <MarketName to={`/markets/${market.id}`}>
                        {market.name}
                      </MarketName>
                      {market.products.items && (
                        <CountBadge>
                          {market.products.items.length} items
                        </CountBadge>
                      )}
                    </MarketNameWrap>

                    {market.tags && market.tags.length > 0 && (
                      <TagRow>
                        {market.tags.map(tag => (
                          <TagPill key={tag}>{tag}</TagPill>
                        ))}
                      </TagRow>
                    )}
                  </MarketHeader>

                  <ProductGrid>
                    {market.products.items && market.products.items.map(product => (
                      <ProductCard key={product.id} product={product} />
                    ))}

                    <ViewAll to={`/markets/${market.id}`}>
                      View all products →
                    </ViewAll>
                  </ProductGrid>
                </MarketBlock>
              ))}
            </MarketsWrap>
            </ProductSearch>
          </>
        )
      }}
    </Connect>
  )
};

export default MarketList;
